import React, { useState } from 'react';
import { ArrowLeft, Plus, Clock } from 'lucide-react';
import ClientSection from './components/ClientSection';
import ItemsList from './components/ItemsList';
import TotalSection from './components/TotalSection';
import SaleModal from '../LotsSelection/components/SaleModal';
import NoClientConfirmationModal from './components/NoClientConfirmationModal';
import { usePrinterWebSocket } from '../../../../../../hooks/usePrinterWebSocket';
import { useBusiness } from '../../../../../../contexts/BusinessContext';

const GhostItemCard = ({ onClick }) => (
  <button
    onClick={onClick}
    className="w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-400 dark:hover:border-gray-500 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 bg-white/50 dark:bg-gray-800/50"
  >
    <Plus className="w-5 h-5" />
    <span>Agregar otro producto</span>
  </button>
);

const ReviewSale = ({ 
  items = [], 
  onAddMore, 
  onEditItem, 
  onDeleteItem,
  onSelectClient,
  onBack,
  onConfirm,
  client = null
}) => {
  const [isPending, setIsPending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showNoClientModal, setShowNoClientModal] = useState(false);
  const [pendingSaleStatus, setPendingSaleStatus] = useState(null);
  const { business } = useBusiness();

  const formatSaleDataForPrint = (saleData) => {
    if (!saleData || !saleData.items || saleData.items.length === 0) {
      return "VENTA SIN PRODUCTOS";
    }

    const businessName = business?.name || "TIBO APP"; // Use actual business name from context
    const saleNumber = "VENTA #" + new Date().getTime().toString().slice(-4);
    const formattedDate = new Date().toLocaleString();

    let text = `================================\n`;
    text += `         ${businessName.toUpperCase()}        \n`;
    text += `================================\n\n`;
    text += `${saleNumber}\n`;
    text += `${formattedDate}\n`;
    text += `--------------------------------\n`;
    text += `PRODUCTO      CANT    PRECIO    TOTAL\n`;
    text += `--------------------------------\n`;

    saleData.items.forEach(item => {
      const productName = item.title.substring(0, 14);
      const quantity = item.quantity.toString().padEnd(4);
      const unitPrice = item.unitPrice.toFixed(2).padEnd(8);
      const total = (item.quantity * item.unitPrice).toFixed(2);
      text += `${productName}${quantity}${unitPrice}${total}\n`;
    });

    text += `--------------------------------\n`;

    const subtotal = saleData.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    text += `SUBTOTAL:                $${subtotal.toFixed(2)}\n`;

    const totalDeposit = saleData.items.reduce((sum, item) => sum + (item.quantity * (item.deposit || 0)), 0);
    if (totalDeposit > 0) {
      text += `SEÑA ENVASES:            $${totalDeposit.toFixed(2)}\n`;
    }

    const totalAmount = subtotal + totalDeposit;
    text += `TOTAL:                     $${totalAmount.toFixed(2)}\n`;

    if (saleData.client) {
      text += `--------------------------------\n`;
      text += `CLIENTE: ${saleData.client.name || 'No especificado'}\n`;
    }

    text += `================================\n`;
    text += `================================\n\n\n\n`;

    return text;
  };

  const handleConfirm = async (status) => {
    if (!client && status === 'confirmed') {
      setPendingSaleStatus(status);
      setShowNoClientModal(true);
      return;
    }

    setIsLoading(true);
    try {
      await onConfirm({ status, client, items });
      
      console.log('=== DEBUG VENTA ===');
      console.log('Business:', {
        id: business?.id,
        name: business?.name
      });
      console.log('Items:', items.length);
      console.log('Cliente:', client);

    } catch (error) {
      console.error('Error en handleConfirm:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmWithoutClient = async () => {
    setShowNoClientModal(false);
    setIsLoading(true);
    try {
      await onConfirm({ status: pendingSaleStatus, client: null, items });
    } catch (error) {
      console.error('Error en handleConfirmWithoutClient:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
      setPendingSaleStatus(null);
    }
  };

  const handleAddClient = () => {
    setShowNoClientModal(false);
    const clientSection = document.querySelector('.client-section');
    if (clientSection) {
      clientSection.scrollIntoView({ behavior: 'smooth' });
      const clientButton = clientSection.querySelector('button');
      if (clientButton) {
        clientButton.focus();
        clientButton.click();
      }
    }
  };

  const handleEditItemClick = (index) => {
    const item = items[index];
    setEditingItem({
      id: item.lotId,
      title: item.title,
      unit: item.unit,
      quantity: item.quantity,
      suggestedPrice: item.unitPrice,
      is_lightning_lot: item.is_lightning_lot,
      container_deposit: item.container_deposit,
      initialQuantity: item.quantity?.toString(),
      initialUnitPrice: item.unitPrice?.toString(),
      initialDeposit: item.deposit?.toString()
    });
    setEditingIndex(index);
  };

  const handleEditConfirm = (updatedItem) => {
    onEditItem(editingIndex, updatedItem);
    setEditingItem(null);
    setEditingIndex(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 z-10">
        <div className="p-4">
          <div className="flex items-center mb-4">
            <button 
              className="mr-3 text-gray-900 dark:text-white"
              onClick={onBack}
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Revisar Venta</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        <ClientSection 
          client={client}
          onSelectClient={onSelectClient}
          className="client-section"
        />

        <div className="space-y-2">
          <h2 className="font-medium text-gray-900 dark:text-white">Productos</h2>
          <div className="space-y-3">
            <ItemsList 
              items={items}
              onEditItem={handleEditItemClick}
              onDeleteItem={onDeleteItem}
            />
            <GhostItemCard onClick={onAddMore} />
          </div>
        </div>

        <TotalSection items={items} />
      </div>

      {/* Bottom Actions */}
      {items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700 p-4 flex gap-3">
          <button
            onClick={() => handleConfirm('pending')}
            disabled={isLoading}
            className="py-6 px-4 bg-amber-500 dark:bg-amber-600 text-white rounded-lg disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 hover:bg-amber-600 dark:hover:bg-amber-700 transition-colors relative flex items-center gap-2"
          >
            {isLoading && isPending ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Guardando...
              </div>
            ) : (
              <>
                <Clock className="w-5 h-5" />
                <span>Pendiente</span>
              </>
            )}
          </button>
          <button
            onClick={() => {
              setIsPending(false);
              handleConfirm('confirmed');
            }}
            disabled={isLoading}
            className="flex-1 py-6 bg-blue-500 dark:bg-blue-600 text-white rounded-lg disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors relative"
          >
            {isLoading && !isPending ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Confirmando...
              </div>
            ) : (
              'Confirmar venta'
            )}
          </button>
        </div>
      )}

      {/* Modal de edición */}
      {editingItem && (
        <SaleModal
          lot={editingItem}
          onClose={() => {
            setEditingItem(null);
            setEditingIndex(null);
          }}
          onConfirm={handleEditConfirm}
        />
      )}

      {/* Modal de confirmación sin cliente */}
      {showNoClientModal && (
        <NoClientConfirmationModal
          onClose={() => setShowNoClientModal(false)}
          onConfirmWithoutClient={handleConfirmWithoutClient}
          onAddClient={handleAddClient}
        />
      )}
    </div>
  );
}

export default ReviewSale; 